import { Calendar } from '@fullcalendar/core';
import { setPageContext } from "../utils/pageContext.js";
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { client } from "../api/client.js";
import { renderPreview } from "../ui/previews.js";
import { renderPageHeader } from "../ui/pageHeader.js";
import { FailedPostsDrawer } from "../ui/failedPostsDrawer.js";
import { openMediaLibrary } from "./create-social.js";
import { openGenerateMonthWizard } from '../ui/generateMonthWizard.js';
import { openPersonalEventModal } from '../ui/personalEventModal.js';
import { isComingSoon } from '../data/comingSoon.js';
import { renderAiAssistToolbar, handleAiAssistClick } from '../ui/aiAssistToolbar.js';
import { renderCharCounter, updateCharCounter } from '../ui/charCounter.js';
import { toDatetimeLocalValue } from '../utils/date.js';

export class CalendarPage {
    constructor() {
        this.calendar = null;
        this.selectedItem = null;
        this.view = 'dayGridMonth';
        this.items = [];
        this.filteredItems = [];
        this.brandName = window.__auth__?.name || "Your Brand";

        // Filtering State
        this.selectedPlatforms = new Set(['facebook', 'instagram', 'x', 'linkedin', 'tiktok', 'youtube', 'pinterest', 'reddit', 'blog', 'website']);
        this.selectedCampaign = 'ALL';
        this.selectedStatus = 'ALL';
        this.derivedCampaigns = [];
        this.derivedStatuses = [];
        // Personal reminders (own table) — toggled by a separate chip from
        // platform filters so they aren't dropped when filtering platforms.
        this.showPersonal = true;
        this.personalEvents = [];

        // Enterprise state
        this.saveState = 'idle'; // idle | saving | saved | error
        this.lastRequestId = null;
        this.lastErrorMessage = null;
        this.lastWriteAction = null; // { type, id, before, after, patch }

        // Toast stack (created once on mount)
        this._toastStack = null;

        // Refetch queue — "last change wins" semantics (Phase 5C.3)
        this._refetchInFlight = false;
        this._refetchQueued = false;
        this._refetchQueuedReason = null;
    }

    async mount(el) {
                setPageContext({ page: "calendar" });
        this.container = el;
        this._ensureToastStack();
        this.renderShell();
        // Init calendar immediately — datesSet fires on first render and drives
        // the initial data load for the currently visible window.
        this.initCalendar();

        // Failed Posts badge + drawer (Phase 5C.2D)
        this._failedDrawer = new FailedPostsDrawer({
            getBadgeAnchor: () => this.container.querySelector('#calendar-failed-badge'),
            toast: (type, msg) => this.toast(type, msg),
            confirm: (opts) => this.confirmDelete(opts),
            onRetry: async () => {},
            onCancel: async () => {},
        });
        await this._failedDrawer.init();
    }

    // ─── Toast Stack ────────────────────────────────────────────────────────────
    _ensureToastStack() {
        if (document.getElementById('calendar-toast-stack')) {
            this._toastStack = document.getElementById('calendar-toast-stack');
            return;
        }
        const stack = document.createElement('div');
        stack.id = 'calendar-toast-stack';
        document.body.appendChild(stack);
        this._toastStack = stack;
    }

    /**
     * Show a toast notification — XSS-safe (no innerHTML interpolation).
     * @param {'success'|'error'|'info'} type
     * @param {string} message
     * @param {{ timeout?: number }} options
     */
    toast(type, message, { timeout = 4000 } = {}) {
        this._ensureToastStack();

        // Build structure with DOM nodes only — textContent is XSS-safe
        const item = document.createElement('div');
        item.className = `cal-toast cal-toast--${type}`;
        item.setAttribute('role', 'status');
        item.setAttribute('aria-live', 'polite');

        const msgSpan = document.createElement('span');
        msgSpan.className = 'cal-toast__msg';
        msgSpan.textContent = String(message || '');
        item.appendChild(msgSpan);

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'cal-toast__close';
        closeBtn.setAttribute('aria-label', 'Dismiss');
        closeBtn.textContent = '\u00d7'; // ×
        item.appendChild(closeBtn);

        const dismiss = () => {
            item.classList.add('cal-toast--out');
            setTimeout(() => item.remove(), 300);
        };

        closeBtn.onclick = dismiss;
        this._toastStack.appendChild(item);

        // Animate in
        requestAnimationFrame(() => item.classList.add('cal-toast--in'));

        if (timeout > 0) {
            setTimeout(dismiss, timeout);
        }
    }

    /**
     * Show an accessible in-app confirm dialog — XSS-safe (no innerHTML interpolation).
     * @returns {Promise<boolean>} Resolves true if confirmed, false if cancelled.
     */
    confirmDelete({ title = 'Confirm Delete', message = 'Are you sure you want to delete this item?' } = {}) {
        return new Promise((resolve) => {
            let settled = false;
            const cleanup = (result) => {
                if (settled) return;
                settled = true;
                document.removeEventListener('keydown', onKey);
                overlay.remove();
                resolve(result);
            };

            // ── Build DOM with textContent only (XSS-safe) ──
            const overlay = document.createElement('div');
            overlay.className = 'cal-modal-overlay';

            const dialog = document.createElement('div');
            dialog.className = 'cal-modal';
            dialog.setAttribute('role', 'dialog');
            dialog.setAttribute('aria-modal', 'true');
            dialog.setAttribute('aria-labelledby', 'cal-modal-title');
            dialog.setAttribute('aria-describedby', 'cal-modal-desc');

            // Header
            const header = document.createElement('div');
            header.className = 'cal-modal__header';
            const titleEl = document.createElement('h3');
            titleEl.id = 'cal-modal-title';
            titleEl.className = 'cal-modal__title';
            titleEl.textContent = String(title);
            header.appendChild(titleEl);

            // Body
            const body = document.createElement('div');
            body.className = 'cal-modal__body';
            const msgEl = document.createElement('p');
            msgEl.id = 'cal-modal-desc';
            msgEl.className = 'cal-modal__msg';
            msgEl.textContent = String(message);
            body.appendChild(msgEl);

            // Footer buttons
            const footer = document.createElement('div');
            footer.className = 'cal-modal__footer';

            const cancelBtn = document.createElement('button');
            cancelBtn.type = 'button';
            cancelBtn.id = 'cal-modal-cancel';
            cancelBtn.className = 'cal-modal__btn cal-modal__btn--cancel';
            cancelBtn.textContent = 'Cancel';
            cancelBtn.onclick = () => cleanup(false);

            const confirmBtn = document.createElement('button');
            confirmBtn.type = 'button';
            confirmBtn.id = 'cal-modal-confirm';
            confirmBtn.className = 'cal-modal__btn cal-modal__btn--delete';
            confirmBtn.textContent = 'Delete';
            confirmBtn.onclick = () => cleanup(true);

            footer.appendChild(cancelBtn);
            footer.appendChild(confirmBtn);

            dialog.appendChild(header);
            dialog.appendChild(body);
            dialog.appendChild(footer);
            overlay.appendChild(dialog);

            // Esc only — no global Enter->confirm (unsafe)
            const onKey = (e) => {
                if (e.key === 'Escape') cleanup(false);
            };

            // Click outside the dialog itself cancels
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) cleanup(false);
            });

            document.addEventListener('keydown', onKey);
            document.body.appendChild(overlay);

            // Focus Cancel by default (safer: avoids accidental delete on Enter)
            requestAnimationFrame(() => cancelBtn.focus());
        });
    }

    // ─── Loading Skeleton ──────────────────────────────────────────────────────
    renderSkeleton() {
        this.container.innerHTML = `
          <div class="calendar-page" id="page-calendar">
            <header class="page-header">
              <div class="page-shell">
                <div class="header-content">
                  <div class="header-left">
                    <div class="skeleton skeleton-text" style="width:120px;height:12px;margin-bottom:8px;"></div>
                    <div class="skeleton skeleton-title" style="width:160px;"></div>
                    <div class="skeleton skeleton-text" style="width:260px;margin-top:6px;"></div>
                  </div>
                </div>
              </div>
            </header>
            <div class="calendar-container page-shell page-content">
              <div class="skeleton skeleton-row" style="height:40px;margin-bottom:16px;border-radius:8px;"></div>
              <div class="calendar-skeleton-grid">
                ${Array.from({length: 35}, () => '<div class="skeleton skeleton-cell"></div>').join('')}
              </div>
            </div>
          </div>
        `;
    }

    // ─── Shell ───────────────────────────────────────────────────────────────────
    renderShell() {
        this.container.innerHTML = `
          <div class="calendar-page" id="page-calendar">
            ${renderPageHeader({
              eyebrow: "CONTENT PIPELINE",
              title: "Calendar",
              subtitle: "Your content blueprint — mapped by month.",
              tourPlaylist: 'calendar',
              rightHtml: `
                    <div class="calendar-header-actions" style="display: flex; align-items: center; gap: 12px;">
                      <div class="calendar-view-toggle segmented-control">
                        <button class="segmented-item ${this.view === 'dayGridMonth' ? 'active' : ''}" data-view="dayGridMonth">Month</button>
                        <button class="segmented-item ${this.view === 'timeGridWeek' ? 'active' : ''}" data-view="timeGridWeek">Week</button>
                        <button class="segmented-item ${this.view === 'timeGridDay' ? 'active' : ''}" data-view="timeGridDay">Day</button>
                      </div>
                      <a href="#create" class="btn btn-glass btn-sm">+ Schedule Post</a>
                      <button id="calendar-add-reminder" class="btn btn-glass btn-sm" data-testid="calendar-add-reminder" type="button">+ Reminder</button>
                      <span id="calendar-failed-badge"></span>
                      <div class="calendar-toolbar" style="display: flex; align-items: center; gap: 12px;">
                        <button id="calendar-undo" class="btn btn-secondary btn-undo" data-icon="undo" ${!this.lastWriteAction ? 'disabled' : ''} aria-label="Undo" title="Undo"></button>
                        <div id="calendar-savepill" class="calendar-savepill status-${this.saveState}">
                          ${this.renderSaveStatus()}
                        </div>
                      </div>
                    </div>
              `
            })}

            <div class="calendar-container page-shell page-content">
               <nav class="calendar-filter-bar">
                    <div class="platform-chips">
                        <button class="chip-btn ${this.selectedPlatforms.has('facebook') ? 'active' : ''} plat-fb" data-platform="facebook"><span class="dot"></span>Facebook</button>
                        <button class="chip-btn ${this.selectedPlatforms.has('instagram') ? 'active' : ''} plat-ig" data-platform="instagram"><span class="dot"></span>Instagram</button>
                        <button class="chip-btn ${this.selectedPlatforms.has('x') ? 'active' : ''} plat-x" data-platform="x"><span class="dot"></span>X</button>
                        <button class="chip-btn ${this.selectedPlatforms.has('linkedin') ? 'active' : ''} plat-li" data-platform="linkedin"><span class="dot"></span>LinkedIn</button>
                        <button class="chip-btn ${this.selectedPlatforms.has('tiktok') ? 'active' : ''} plat-tt" data-platform="tiktok"><span class="dot"></span>TikTok<span class="coming-soon-tag coming-soon-tag--chip">Soon</span></button>
                        <button class="chip-btn ${this.selectedPlatforms.has('youtube') ? 'active' : ''} plat-yt" data-platform="youtube"><span class="dot"></span>YouTube</button>
                        <button class="chip-btn ${this.selectedPlatforms.has('pinterest') ? 'active' : ''} plat-pin" data-platform="pinterest"><span class="dot"></span>Pinterest</button>
                        <button class="chip-btn ${this.selectedPlatforms.has('reddit') ? 'active' : ''} plat-rd" data-platform="reddit"><span class="dot"></span>Reddit</button>
                        <button class="chip-btn ${this.selectedPlatforms.has('blog') ? 'active' : ''} plat-blog" data-platform="blog"><span class="dot"></span>WordPress</button>
                        <button class="chip-btn ${this.selectedPlatforms.has('website') ? 'active' : ''} plat-website" data-platform="website"><span class="dot"></span>Website</button>
                        <button class="chip-btn ${this.showPersonal ? 'active' : ''} plat-personal" data-personal-toggle="1" data-testid="calendar-personal-chip" type="button"><span class="dot"></span>Reminders</button>
                    </div>
                </nav>

                <main class="calendar-main">
                    <div id="calendar-surface" class="calendar-surface"></div>
                </main>
            </div>

            <!-- Edit Drawer -->
            <div id="calendar-drawer" class="edit-drawer"></div>
            <div id="calendar-overlay" class="drawer-backdrop" data-action="close-drawer"></div>
          </div>
        `;

        this.bindEvents();
    }

    bindEvents() {
        // View Toggle (segmented control in page header)
        this.container.querySelectorAll('.calendar-view-toggle .segmented-item').forEach(btn => {
            btn.onclick = () => {
                this.changeView(btn.dataset.view);
                this._syncViewToggle();
            };
        });

        // Platform Chips
        this.container.querySelectorAll('.chip-btn').forEach(btn => {
            btn.onclick = async () => {
                if (btn.dataset.personalToggle) {
                    this.showPersonal = !this.showPersonal;
                    btn.classList.toggle('active', this.showPersonal);
                    this.refreshCalendarEvents();
                    return;
                }
                const platform = btn.getAttribute('data-platform');
                if (this.selectedPlatforms.has(platform)) {
                    this.selectedPlatforms.delete(platform);
                } else {
                    this.selectedPlatforms.add(platform);
                }
                this.updateFilterUI();
                this.refreshCalendarEvents(); // instant client-side update
                await this.refetchCurrentRange('platform-change'); // server sync
            };
        });

        // "+ Reminder" header button
        const reminderBtn = this.container.querySelector('#calendar-add-reminder');
        if (reminderBtn) {
            reminderBtn.onclick = async () => {
                const result = await openPersonalEventModal({
                    brandId: window.__auth__?.id || null,
                });
                if (result?.action === 'saved' && result.item) {
                    this.personalEvents = this._upsertPersonal(this.personalEvents, result.item);
                    this.refreshCalendarEvents();
                    this.toast('success', 'Reminder added.');
                }
            };
        }

        const undoBtn = this.container.querySelector('#calendar-undo');
        if (undoBtn) {
            undoBtn.onclick = () => this.performUndo();
        }

        const overlay = this.container.querySelector('#calendar-overlay');
        if (overlay) {
            overlay.onclick = () => this.closeDrawer();
        }
    }

    _syncViewToggle() {
        const cur = this.calendar?.view?.type || this.view;
        this.container.querySelectorAll('.calendar-view-toggle .segmented-item').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.view === cur);
        });
    }

    updateFilterUI() {
        this.container.querySelectorAll('.chip-btn').forEach(btn => {
            const p = btn.getAttribute('data-platform');
            if (p === 'all') {
                btn.classList.toggle('active', this.selectedPlatforms.size === 5);
            } else {
                btn.classList.toggle('active', this.selectedPlatforms.has(p));
            }
        });
    }

    /**
     * Refetch the currently visible range with updated server filters.
     * "Last change wins" semantics: a call arriving while a fetch is in flight
     * queues itself (overwriting any prior queued reason) rather than being
     * silently dropped. The loop drains the queue before releasing the lock.
     *
     * Called ONLY from filter change handlers — NOT from datesSet.
     * loadDataForRange() already calls refreshCalendarEvents() internally.
     */
    async refetchCurrentRange(reason) {
        if (!this.calendar) return;

        // If a fetch is already running, queue this request and return.
        // The running loop will pick it up after it finishes.
        if (this._refetchInFlight) {
            this._refetchQueued = true;
            this._refetchQueuedReason = reason;
            return;
        }

        this._refetchInFlight = true;
        try {
            // Loop drains any requests that queued up while we were fetching
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const start = this.calendar.view.activeStart;
                const end = this.calendar.view.activeEnd;
                await this.loadDataForRange(start, end);

                if (this._refetchQueued) {
                    // Consume the queued request and loop once more
                    reason = this._refetchQueuedReason || 'queued';
                    this._refetchQueued = false;
                    this._refetchQueuedReason = null;
                } else {
                    break;
                }
            }
        } catch (err) {
            // loadDataForRange already toasts; safety-net for unexpected throws
            console.error(`[CalendarPage] refetchCurrentRange(${reason}) unexpected error`, err);
        } finally {
            this._refetchInFlight = false;
        }
    }


    /**
     * Load items for a specific date window (replaces the old 2-year dump).
     * Merges new items into this.items by id so navigations accumulate without
     * duplicates, then refreshes the FullCalendar event list.
     */
    async loadDataForRange(startDate, endDate) {
        try {
            // Task C: pass platform/status to server to reduce payload when filters are active.
            // Campaign intentionally omitted — no schema FK (see Phase 5C notes).
            const params = {
                start: startDate instanceof Date ? startDate.toISOString() : startDate,
                end: endDate instanceof Date ? endDate.toISOString() : endDate,
            };
            // Only send platform if NOT all 5 selected (server default returns all)
            if (this.selectedPlatforms.size > 0 && this.selectedPlatforms.size < 5) {
                params.platform = Array.from(this.selectedPlatforms).join(',');
            }
            // Only send status if a specific one is selected
            if (this.selectedStatus && this.selectedStatus !== 'ALL') {
                params.status = this.selectedStatus;
            }

            // Fan out the schedule fetch and the personal-events fetch so
            // reminders show up alongside drafts on the same loadDataForRange tick.
            const personalParams = { start: params.start, end: params.end };
            const [data, personalRes] = await Promise.all([
                client.getScheduleCalendar(params),
                client.getPersonalEvents(personalParams).catch(() => ({ items: [] })),
            ]);

            const incoming = data.items || [];

            // Merge: build map of existing items by id, then upsert incoming
            const map = new Map(this.items.map(i => [i.id, i]));
            for (const item of incoming) {
                map.set(item.id, item);
            }
            this.items = Array.from(map.values());

            const incomingPersonal = (personalRes && personalRes.items) || [];
            const pmap = new Map(this.personalEvents.map(i => [i.id, i]));
            for (const item of incomingPersonal) pmap.set(item.id, item);
            this.personalEvents = Array.from(pmap.values());

            this.deriveFilters();
            this.updateDerivedFilterSelects(); // Task A: sync dropdowns without shell re-render
            this.refreshCalendarEvents();
        } catch (err) {
            console.error('Failed to load calendar data for range', err);
            this.toast('error', 'Could not load calendar data. Check your connection.');
        }
    }

    deriveFilters() {
        const campaigns = new Set();
        const statuses = new Set();
        this.items.forEach(item => {
            const c = item.campaign_name || item.campaign || "No campaign";
            campaigns.add(c);
            if (item.status) statuses.add(item.status);
        });
        this.derivedCampaigns = Array.from(campaigns).sort();
        this.derivedStatuses = Array.from(statuses).sort();
    }

    /**
     * Task A: Rebuild #filter-campaign and #filter-status option lists in-place
     * after a date-window load — does NOT call renderShell().
     * Preserves the currently selected value; falls back to ALL if it no longer exists.
     */
    updateDerivedFilterSelects() {
        // ── Campaign select ──
        const campaignEl = this.container?.querySelector('#filter-campaign');
        if (campaignEl) {
            // Check whether current selection still exists in derived list
            const campStillValid = this.selectedCampaign === 'ALL' ||
                this.derivedCampaigns.includes(this.selectedCampaign);
            if (!campStillValid) this.selectedCampaign = 'ALL';

            // Rebuild options
            campaignEl.innerHTML = '';
            const allCampOpt = document.createElement('option');
            allCampOpt.value = 'ALL';
            allCampOpt.textContent = 'All Campaigns';
            campaignEl.appendChild(allCampOpt);

            for (const c of this.derivedCampaigns) {
                const opt = document.createElement('option');
                opt.value = c;
                opt.textContent = c;
                if (c === this.selectedCampaign) opt.selected = true;
                campaignEl.appendChild(opt);
            }
            campaignEl.value = this.selectedCampaign;
        }

        // ── Status select ──
        const statusEl = this.container?.querySelector('#filter-status');
        if (statusEl) {
            // Check whether current selection still exists in derived list
            const statusStillValid = this.selectedStatus === 'ALL' ||
                this.derivedStatuses.includes(this.selectedStatus);
            if (!statusStillValid) this.selectedStatus = 'ALL';

            // Rebuild options
            statusEl.innerHTML = '';
            const allStatOpt = document.createElement('option');
            allStatOpt.value = 'ALL';
            allStatOpt.textContent = 'All Statuses';
            statusEl.appendChild(allStatOpt);

            for (const s of this.derivedStatuses) {
                const opt = document.createElement('option');
                opt.value = s;
                opt.textContent = s;
                if (s === this.selectedStatus) opt.selected = true;
                statusEl.appendChild(opt);
            }
            statusEl.value = this.selectedStatus;
        }
    }

    initCalendar() {
        const calendarEl = this.container.querySelector('#calendar-surface');
        if (!calendarEl) return;

        this.calendar = new Calendar(calendarEl, {
            plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
            initialView: this.view,
            headerToolbar: {
                left: 'prev,title,next',
                center: '',
                right: 'today'
            },
            editable: true,
            height: 'auto',
            contentHeight: 'auto',
            expandRows: false,
            // Force all events to render as solid colored blocks (not invisible dots)
            eventDisplay: 'block',
            // Month-view density: cap cells at 4 visible events, show "+N more" popover
            dayMaxEvents: 4,
            dayMaxEventRows: true,
            moreLinkClick: 'popover',
            events: this.getFilteredEvents(),
            eventContent: (arg) => this.renderEventContent(arg),
            eventDidMount: (info) => {
                const status = info.event.extendedProps?.status || 'draft';
                info.el.setAttribute('data-status', status);
                info.el.classList.add('status-' + status);
            },
            // datesSet fires on initial render AND every navigation (prev/next/view change)
            // This drives date-window loading — replaces the old 2-year prefetch.
            datesSet: (info) => {
                this.loadDataForRange(info.start, info.end);
                this._syncViewToggle();
            },
            dateClick: (info) => {
                // Click on empty day → open Content Studio with date pre-filled
                window.location.hash = `#create?scheduleDate=${info.dateStr}`;
            },
            eventClick: (info) => {
                info.jsEvent.preventDefault();
                if (String(info.event.id).startsWith('pe:')) {
                    this.openPersonalEditor(info.event);
                    return;
                }
                try {
                    this.openDrawer(info.event);
                } catch (err) {
                    console.error('[CALENDAR] openDrawer failed:', err, 'event:', info.event?.extendedProps);
                }
            },
            eventDrop: (info) => this.handleEventDrop(info),
            eventResize: (info) => this.handleEventDrop(info),
            // Phase 31H: Mobile responsive — swap to listWeek below 768px
            windowResize: (view) => {
                const isMobile = window.innerWidth < 768;
                const currentView = this.calendar.view.type;
                if (isMobile && currentView !== 'listWeek') {
                    this.calendar.changeView('listWeek');
                } else if (!isMobile && currentView === 'listWeek') {
                    this.calendar.changeView('dayGridMonth');
                }
            },
        });

        // Phase 31H: Apply the initial mobile view on first render
        if (window.innerWidth < 768 && this.calendar.view.type !== 'listWeek') {
            this.calendar.changeView('listWeek');
        }

        this.calendar.render();
        this._syncViewToggle();
    }

    getFilteredEvents() {
        const filtered = this.items.filter(item => {
            const platMatch = this.selectedPlatforms.has((item.platform || "").toLowerCase());
            const camp = item.campaign_name || item.campaign || "No campaign";
            const campMatch = this.selectedCampaign === 'ALL' || camp === this.selectedCampaign;
            const statusMatch = this.selectedStatus === 'ALL' || item.status === this.selectedStatus;
            return platMatch && campMatch && statusMatch;
        });

        const events = filtered.map(item => {
            const platClass = this.platformClass(item.platform);
            const status = (item.status || 'pending').toLowerCase();
            const statusClass = `status-${status}`;
            return {
                id: String(item.id), // CAL-SAVE-002: always string — FC event.id is always a string
                title: item.title || (item.content_type === 'blog' ? 'Blog Post' : this.truncate(item.content || '', 40)),
                start: item.scheduled_for,
                allDay: false,
                className: `${platClass} ${statusClass}`,
                extendedProps: { ...item, kind: 'post' },
            };
        });

        // Personal reminders — `pe:` id prefix lets the click + drop dispatch
        // route them through the personalEvents API instead of scheduledPosts.
        if (this.showPersonal) {
            for (const ev of this.personalEvents) {
                const status = (ev.status || 'open').toLowerCase();
                events.push({
                    id: `pe:${ev.id}`,
                    title: ev.title || 'Reminder',
                    start: ev.starts_at,
                    end: ev.duration_minutes ? new Date(new Date(ev.starts_at).getTime() + ev.duration_minutes * 60 * 1000).toISOString() : undefined,
                    allDay: false,
                    className: `plat-personal status-${status}`,
                    extendedProps: { ...ev, kind: 'personal', platform: 'personal' },
                });
            }
        }
        return events;
    }

    _upsertPersonal(arr, item) {
        const next = arr.filter(x => x.id !== item.id);
        next.push(item);
        return next;
    }

    platformClass(platform) {
        const p = (platform || "").toLowerCase();
        if (p === "personal") return "plat-personal";
        if (p.includes("facebook")) return "plat-facebook";
        if (p.includes("instagram")) return "plat-instagram";
        if (p === "x" || p.includes("twitter")) return "plat-x";
        if (p.includes("linkedin")) return "plat-linkedin";
        if (p.includes("tiktok")) return "plat-tt";
        if (p.includes("youtube")) return "plat-yt";
        if (p.includes("pinterest")) return "plat-pin";
        if (p.includes("reddit")) return "plat-rd";
        if (p.includes("website")) return "plat-website";
        if (p.includes("blog")) return "plat-blog";
        return "plat-default";
    }

    /**
     * Returns a short human-readable platform label for event card badges.
     */
    _platformLabel(platform) {
        const p = (platform || '').toLowerCase();
        if (p === 'personal') return '★';
        if (p.includes('facebook')) return 'FB';
        if (p.includes('instagram')) return 'IG';
        if (p === 'x' || p.includes('twitter')) return 'X';
        if (p.includes('linkedin')) return 'IN';
        if (p.includes('tiktok')) return 'TT';
        if (p.includes('youtube')) return 'YT';
        if (p.includes('pinterest')) return 'PIN';
        if (p.includes('reddit')) return 'RD';
        if (p.includes('website')) return 'WB';
        if (p.includes('blog')) return 'WP';
        return '?';
    }

    _platformFullName(platform) {
        const p = (platform || '').toLowerCase();
        if (p === 'personal') return 'Reminder';
        if (p.includes('facebook')) return 'Facebook';
        if (p.includes('instagram')) return 'Instagram';
        if (p === 'x' || p.includes('twitter')) return 'X';
        if (p.includes('linkedin')) return 'LinkedIn';
        if (p.includes('tiktok')) return 'TikTok';
        if (p.includes('youtube')) return 'YouTube';
        if (p.includes('pinterest')) return 'Pinterest';
        if (p.includes('reddit')) return 'Reddit';
        if (p.includes('website')) return 'Website';
        if (p.includes('blog')) return 'WordPress';
        return platform || '?';
    }

    /** User-friendly label for a scheduled-post status. */
    _statusLabel(status) {
        const map = {
            pending: 'Pending',
            scheduled: 'Scheduled',
            publishing: 'Publishing',
            published: 'Published',
            failed: 'Failed',
            dead_letter: 'Needs Attention',
            cancelled: 'Cancelled',
            canceled: 'Cancelled',
            draft: 'Draft'
        };
        return map[(status || '').toLowerCase()] || (status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }

    /**
     * FullCalendar eventContent renderer — simplified V3 style.
     * Shows "9am Facebook" format — clean colored bars with time + platform name.
     */
    renderEventContent(arg) {
        const props = arg.event.extendedProps || {};
        const platName = this._platformFullName(props.platform);
        const timeText = arg.timeText || '';
        const fullTitle = arg.event.title || '';

        const wrapper = document.createElement('div');
        wrapper.className = 'cal-evt';
        wrapper.title = [timeText, platName, fullTitle].filter(Boolean).join(' \u2022 ');

        // Platform color dot
        const dot = document.createElement('span');
        dot.className = `cal-evt__dot cal-evt__dot--${(props.platform || '').toLowerCase()}`;
        wrapper.appendChild(dot);

        const label = document.createElement('span');
        label.className = 'cal-evt__label';
        const displayTitle = fullTitle
            ? (fullTitle.length > 25 ? fullTitle.substring(0, 25).trim() + '\u2026' : fullTitle)
            : platName;
        label.textContent = timeText ? `${timeText} ${displayTitle}` : displayTitle;
        wrapper.appendChild(label);

        // Autopilot provenance badge — content_items.source is stamped with an
        // 'autopilot...' prefix (e.g. 'autopilot', 'autopilot:promo_cascade',
        // 'autopilot:calendar_fill') by the weekly cycle / cascade / calendar-fill
        // paths. Flag those events so the calendar shows what the bot created.
        if (props.source && String(props.source).startsWith('autopilot')) {
            const badge = document.createElement('span');
            badge.className = 'cal-evt__badge cal-evt__badge--autopilot';
            badge.textContent = 'AP';
            badge.title = 'Created by Autopilot';
            wrapper.appendChild(badge);
        }

        return { domNodes: [wrapper] };
    }

    refreshCalendarEvents() {
        if (!this.calendar) return;
        const events = this.getFilteredEvents();
        this.calendar.removeAllEvents();
        this.calendar.addEventSource(events);
        this.updateEmptyState(events.length === 0);
    }

    updateEmptyState(isEmpty) {
        const existing = this.container.querySelector('.calendar-empty-state');
        if (existing) existing.remove();

        if (!isEmpty) return;

        const emptyEl = document.createElement('div');
        emptyEl.className = 'calendar-empty-state';
        emptyEl.innerHTML = `
            <div class="calendar-empty-state__inner">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color:var(--primary,#8B5CF6);opacity:0.6;">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <h3>Nothing scheduled yet</h3>
                <p>The fastest path: ask Heist's chat. It plans, drafts, and schedules in one conversation.</p>
                <div class="empty-state-actions">
                    <a href="#memory?tab=chat&prompt=Plan%20a%20week%20of%20posts%20for%20me%20across%20all%20my%20connected%20platforms" class="btn btn-primary btn-sm" data-testid="empty-talk-to-heist">Talk to Heist &rarr;</a>
                    <a href="#create" class="btn btn-secondary btn-sm">Open Content Studio</a>
                    <button class="btn btn-secondary btn-sm" id="gmw-calendar-launch">Generate Your Month</button>
                </div>
            </div>
        `;

        const surface = this.container.querySelector('.calendar-main');
        if (surface) surface.appendChild(emptyEl);

        const gmwBtn = emptyEl.querySelector('#gmw-calendar-launch');
        if (gmwBtn) gmwBtn.addEventListener('click', () => openGenerateMonthWizard());
    }

    changeView(view) {
        this.view = view;
        if (this.calendar) {
            this.calendar.changeView(view);
        }
    }

    openDrawer(event) {
        const props = event.extendedProps || {};
        // Blog/Website/WordPress posts route to the blog editor in Content
        // Studio — the simple calendar drawer can't edit rich HTML content.
        // Detection mirrors Library's isBlogItem: checks type, _source, AND
        // platform because different rows come through with different shapes
        // (content_items legacy rows, blog_posts rows, calendar orphan UNION).
        const plat = String(props.platform || '').toLowerCase();
        const isBlogItem = props._source === 'blog_posts'
            || props.content_type === 'blog_post'
            || props.content_type === 'blog'
            || plat === 'blog'
            || plat === 'website'
            || plat === 'wordpress';
        if (isBlogItem) {
            const blogId = props.blog_post_id || props.content_id || event.id;
            window.location.hash = `#create?mode=blog&blogId=${encodeURIComponent(blogId)}`;
            return;
        }
        // Social posts: open the calendar's own drawer (publish / retry / cancel
        // controls live here). Users can still jump to the full editor via the
        // drawer's "Edit in Library" link inside renderDrawerContent.
        this.selectedItem = { ...props, id: event.id, start: event.start };
        const drawer = this.container.querySelector('#calendar-drawer');
        const overlay = this.container.querySelector('#calendar-overlay');
        this.renderDrawerContent();
        drawer.classList.add('open');
        overlay.classList.add('active');
        document.body.classList.add('drawer-open');
    }

    closeDrawer() {
        const drawer = this.container.querySelector('#calendar-drawer');
        const overlay = this.container.querySelector('#calendar-overlay');
        drawer.classList.remove('open');
        overlay.classList.remove('active');
        document.body.classList.remove('drawer-open');
        this.selectedItem = null;
    }

    renderDrawerContent() {
        const item = this.selectedItem;
        if (!item) return;

        // Initialize drawer media from item's media_json. Historical rows
        // come in a few shapes — array, {urls:[...]}, bare string — so
        // normalize to an array of {url,...} objects the preview can render.
        try {
            let parsed = [];
            if (Array.isArray(item.media_json)) {
                parsed = item.media_json;
            } else if (typeof item.media_json === 'string' && item.media_json.length) {
                parsed = JSON.parse(item.media_json);
            } else if (item.media_json && typeof item.media_json === 'object') {
                parsed = item.media_json;
            }
            if (Array.isArray(parsed)) {
                this.drawerMedia = parsed;
            } else if (parsed && Array.isArray(parsed.urls)) {
                this.drawerMedia = parsed.urls.map(u => typeof u === 'string' ? { url: u, type: 'image' } : u);
            } else if (parsed && parsed.url) {
                this.drawerMedia = [parsed];
            } else {
                this.drawerMedia = [];
            }
        } catch { this.drawerMedia = []; }

        const drawer = this.container.querySelector('#calendar-drawer');
        // Convert UTC-stored date to the user's LOCAL timezone for the
        // datetime-local input. Using toISOString().slice(0,16) was showing
        // UTC time, so a post at 6pm PT (01:00 UTC next day) appeared to
        // be scheduled for 1am on the next day in the drawer.
        const dateStr = toDatetimeLocalValue(item.start);
        const createdDate = item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A';

        drawer.innerHTML = `
            <div class="drawer-header">
                <div class="drawer-title">
                    <h3>Edit Scheduled Post</h3>
                    <span class="pill pill-platform">${String(item.platform || 'POST').toUpperCase()}</span>
                    <span class="pill pill-status">${this._statusLabel(item.status)}</span>
                </div>
                <div class="drawer-header-actions">
                    <button id="drawer-delete-top" class="icon-btn" data-icon="trash" title="Delete Item" aria-label="Delete"></button>
                    <button id="drawer-close" class="icon-btn" data-icon="x" title="Close Drawer" aria-label="Close"></button>
                </div>
            </div>

            <div class="drawer-body">
                <!-- Two-pane layout with dedicated scroll areas -->
                <section class="drawer-editor">
                    <div class="pane-scroll-area">
                        <div class="editor-settings">
                            <div class="setting-item">
                                <label for="drawer-title">Title</label>
                                <input type="text" id="drawer-title" placeholder="Post title..." value="${item.title || ''}">
                            </div>
                            <div class="setting-item">
                                <label for="drawer-datetime">Scheduled At</label>
                                <input type="datetime-local" id="drawer-datetime" value="${dateStr}">
                            </div>
                        </div>

                        <div class="editor-main">
                            <div class="editor-toolbar">
                                <span class="muted">Content</span>
                                <button class="btn btn-secondary btn-sm" id="drawer-copy">Copy</button>
                            </div>
                            ${renderAiAssistToolbar({ showFillAi: true, showFillToAll: false })}
                            <textarea id="edit-content" placeholder="Content text...">${item.content || ''}</textarea>
                            ${renderCharCounter({ platform: item.platform, initialLength: (item.content || '').length, counterId: 'cal-char-counter' })}
                        </div>

                        <div class="setting-item" style="margin-top:16px">
                            <label>Attach Media</label>
                            <input type="file" id="drawer-media-input" accept="image/*,video/*" multiple style="display:none">
                            <label for="drawer-media-input" class="media-zone media-zone--editor drawer-media-zone" id="drawer-media-dropzone" role="button" tabindex="0">
                                <div class="media-zone-ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg></div>
                                <div class="media-zone-lbl">Drop image or video here</div>
                                <div class="media-zone-sub">JPG, PNG, GIF, MP4, MOV</div>
                            </label>
                            <div class="drawer-media-list" id="drawer-media-list">${this._renderDrawerMediaList()}</div>
                            <div class="drawer-media-actions">
                                ${window.__auth__?.workspace_id ? '<button class="media-library-btn" id="cal-open-media-library">Browse Media Library</button>' : ''}
                                <button class="media-library-btn drawer-ai-image-btn" id="cal-gen-ai-image" data-testid="cal-gen-ai-image" title="Generate an AI image from this post's content">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-1px;margin-right:4px;"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                                    Generate AI image
                                </button>
                            </div>
                            <div class="drawer-ai-status" id="cal-ai-status" data-testid="cal-ai-status"></div>
                        </div>

                        ${this._renderPublishZone(item)}

                        <details class="drawer-details">
                            <summary>Details</summary>
                            <ul class="meta-list">
                                <li><strong>ID:</strong> <span class="small-id">${item.id}</span></li>
                                <li><strong>Created:</strong> ${createdDate}</li>
                                <li><strong>Platform:</strong> ${item.platform}</li>
                            </ul>
                        </details>
                    </div>
                </section>

                <section class="drawer-preview">
                    <div class="preview-fixed-header">
                        <span>PREVIEW</span>
                    </div>
                    <div class="pane-scroll-area">
                        <div class="preview-container" id="drawer-preview-mount">
                            ${renderPreview(item.platform, item.content, item.title, this.brandName, this.drawerMedia)}
                        </div>
                    </div>
                </section>
            </div>

            <div class="drawer-footer">
                <button class="btn btn-secondary" id="drawer-footer-close">Close</button>
                <button class="btn btn-primary" id="drawer-save" ${this.saveState === 'saving' ? 'disabled' : ''}>
                    ${this.saveState === 'saving' ? 'Saving...' : (this.saveState === 'saved' ? 'Saved ✓' : 'Save Changes')}
                </button>
            </div>
        `;

        // Bind Drawer Events
        drawer.querySelector('#drawer-close').onclick = () => this.closeDrawer();
        drawer.querySelector('#drawer-footer-close').onclick = () => this.closeDrawer();

        const handleDelete = () => this.handleDelete();
        // Delete is now only in header, matching Library parity
        const deleteBtn = drawer.querySelector('#drawer-delete-top');
        if (deleteBtn) deleteBtn.onclick = handleDelete;

        drawer.querySelector('#drawer-save').onclick = () => this.handleSave();

        // Live Preview Binding (defined early so AI toolbar handler can use it)
        const contentArea = drawer.querySelector('#edit-content');
        const titleInput = drawer.querySelector('#drawer-title');
        const previewMount = drawer.querySelector('#drawer-preview-mount');

        const updatePreview = () => {
            if (!previewMount) return;
            const text = contentArea?.value || '';
            const title = titleInput ? titleInput.value : '';
            previewMount.innerHTML = renderPreview(item.platform, text, title, this.brandName, this.drawerMedia);
            updateCharCounter('cal-char-counter', text.length);
        };

        if (contentArea) contentArea.oninput = updatePreview;
        if (titleInput) titleInput.oninput = updatePreview;

        // AI Assist toolbar
        const assistToolbar = drawer.querySelector('.assist-toolbar');
        const aiBar = drawer.querySelector('.ai-bar');
        if (assistToolbar || aiBar) {
            drawer.addEventListener('click', async (e) => {
                const handled = await handleAiAssistClick(
                    e,
                    () => drawer.querySelector('#edit-content')?.value || '',
                    (text) => {
                        const el = drawer.querySelector('#edit-content');
                        if (el) el.value = text;
                        if (this.selectedItem) this.selectedItem.content = text;
                        // Live-update preview after AI change
                        updatePreview();
                    },
                    item?.platform || 'general',
                    (msg, kind) => {
                        const toast = document.createElement('div');
                        toast.className = `toast toast-${kind}`;
                        toast.textContent = msg;
                        document.body.appendChild(toast);
                        setTimeout(() => toast.remove(), 3000);
                    }
                );
            });
        }

        drawer.querySelector('#drawer-copy').onclick = (e) => {
            const content = drawer.querySelector('#edit-content').value;
            navigator.clipboard.writeText(content);
            const oldText = e.target.textContent;
            e.target.textContent = 'Copied!';
            setTimeout(() => e.target.textContent = oldText, 2000);
        };

        // Publish action bindings
        const publishNowBtn = drawer.querySelector('#drawer-publish-now');
        if (publishNowBtn) publishNowBtn.onclick = () => this.handlePublishNow();

        const retryBtn = drawer.querySelector('#drawer-publish-retry');
        if (retryBtn) retryBtn.onclick = () => this.handleRetryPublish();

        // Browse Media Library
        const mlBtn = drawer.querySelector('#cal-open-media-library');
        if (mlBtn) mlBtn.onclick = () => openMediaLibrary(this);

        // Phase 38 Commit 3 — Generate AI image inline in the calendar drawer.
        // Uses the scheduled post's title + first 200 chars of content as the
        // prompt seed. Result is appended to drawerMedia and the preview
        // re-renders so the user sees the new image immediately.
        const aiBtn = drawer.querySelector('#cal-gen-ai-image');
        const aiStatus = drawer.querySelector('#cal-ai-status');
        if (aiBtn) {
            aiBtn.onclick = async () => {
                if (aiBtn.disabled) return;
                const item = this.selectedItem || {};
                const contentArea = document.querySelector('#edit-content');
                const titleInput = document.querySelector('#drawer-title');
                const text = (contentArea?.value || item.content || '').trim();
                const title = (titleInput?.value || item.title || '').trim();
                const seed = title || text.slice(0, 80) || (item.platform || '').toString();
                if (!seed) {
                    if (aiStatus) {
                        aiStatus.textContent = 'Add a title or content first.';
                        aiStatus.className = 'drawer-ai-status drawer-ai-status--warn';
                    }
                    return;
                }
                const prompt = `Editorial-style image for a ${item.platform || 'social'} post titled "${seed}". ${text.slice(0, 240)}`.trim();
                aiBtn.disabled = true;
                const original = aiBtn.innerHTML;
                aiBtn.textContent = 'Generating…';
                if (aiStatus) {
                    aiStatus.textContent = 'Calling your AI image provider…';
                    aiStatus.className = 'drawer-ai-status drawer-ai-status--info';
                }
                try {
                    const res = await client.generateAiImage({
                        prompt,
                        size: item.platform === 'tiktok' || item.platform === 'instagram_reels' ? '1024x1792' : '1024x1024',
                    });
                    const url = res?.url || res?.media?.url;
                    if (res?.ok !== false && url) {
                        if (!this.drawerMedia) this.drawerMedia = [];
                        this.drawerMedia.push({
                            url,
                            type: 'image',
                            altText: 'AI-generated image',
                            fromLibrary: true,
                        });
                        const mediaList = drawer.querySelector('#drawer-media-list');
                        if (mediaList) mediaList.innerHTML = this._renderDrawerMediaList();
                        updatePreview();
                        if (aiStatus) {
                            aiStatus.textContent = 'Added to the post.';
                            aiStatus.className = 'drawer-ai-status drawer-ai-status--ok';
                        }
                    } else {
                        if (aiStatus) {
                            aiStatus.textContent = res?.error?.message || res?.message || 'Generation failed.';
                            aiStatus.className = 'drawer-ai-status drawer-ai-status--err';
                        }
                    }
                } catch (err) {
                    const body = err?.body || {};
                    let msg = body.error?.message || body.message || err.message || 'Generation failed.';
                    if (body.code === 'NO_IMAGE_PROVIDER' || body.error?.code === 'NO_IMAGE_PROVIDER') {
                        msg = 'Connect an OpenAI key in Settings → AI Settings to enable image generation.';
                    }
                    if (aiStatus) {
                        aiStatus.textContent = msg;
                        aiStatus.className = 'drawer-ai-status drawer-ai-status--err';
                    }
                } finally {
                    aiBtn.disabled = false;
                    aiBtn.innerHTML = original;
                }
            };
        }

        const cancelPublishBtn = drawer.querySelector('#drawer-publish-cancel');
        if (cancelPublishBtn) cancelPublishBtn.onclick = () => this.handleCancelPublish();

        // Media zone bindings
        const mediaInput = drawer.querySelector('#drawer-media-input');
        const mediaDropzone = drawer.querySelector('#drawer-media-dropzone');

        if (mediaInput) {
            mediaInput.onchange = (e) => {
                if (e.target.files.length > 0) this._handleDrawerMediaFiles(e.target.files, updatePreview);
                e.target.value = '';
            };
        }

        if (mediaDropzone) {
            mediaDropzone.addEventListener('dragover', (e) => { e.preventDefault(); e.stopPropagation(); mediaDropzone.classList.add('media-zone--dragover'); });
            mediaDropzone.addEventListener('dragleave', (e) => { e.preventDefault(); e.stopPropagation(); mediaDropzone.classList.remove('media-zone--dragover'); });
            mediaDropzone.addEventListener('drop', (e) => {
                e.preventDefault(); e.stopPropagation();
                mediaDropzone.classList.remove('media-zone--dragover');
                if (e.dataTransfer.files.length > 0) this._handleDrawerMediaFiles(e.dataTransfer.files, updatePreview);
            });
        }

        // Media remove buttons (delegated)
        const mediaList = drawer.querySelector('#drawer-media-list');
        if (mediaList) {
            mediaList.onclick = (e) => {
                const removeBtn = e.target.closest('[data-remove-drawer-media]');
                if (removeBtn) {
                    const idx = parseInt(removeBtn.dataset.removeDrawerMedia);
                    this.drawerMedia.splice(idx, 1);
                    mediaList.innerHTML = this._renderDrawerMediaList();
                    updatePreview();
                }
            };
        }
    }

    _renderDrawerMediaList() {
        if (!this.drawerMedia || this.drawerMedia.length === 0) return '';
        return this.drawerMedia.map((m, i) => {
            // Shape-tolerant: dataUrl (file uploads) || url (server writes).
            const src = m.dataUrl || m.url || m.thumbUrl || '';
            const t = String(m.type || '').toLowerCase();
            const extLooksVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(src);
            const isVideo = t.startsWith('video/') || t === 'video' || (!t && extLooksVideo);
            const alt = m.altText || m.name || m.originalName || m.filename || 'media';
            return `
            <div class="drawer-media-item">
                ${isVideo
                    ? `<video src="${src}" class="drawer-media-thumb" controls preload="metadata"></video>`
                    : `<img src="${src}" class="drawer-media-thumb" alt="${alt}">`
                }
                <button class="drawer-media-remove" data-remove-drawer-media="${i}" title="Remove">&times;</button>
                <div class="drawer-media-name">${alt}</div>
            </div>
        `;
        }).join('');
    }

    _handleDrawerMediaFiles(files, updatePreview) {
        if (!this.drawerMedia) this.drawerMedia = [];
        let remaining = files.length;
        for (const file of files) {
            const reader = new FileReader();
            reader.onload = (e) => {
                this.drawerMedia.push({ name: file.name, type: file.type, dataUrl: e.target.result, size: file.size });
                remaining--;
                if (remaining === 0) {
                    const list = this.container.querySelector('#drawer-media-list');
                    if (list) list.innerHTML = this._renderDrawerMediaList();
                    if (updatePreview) updatePreview();
                }
            };
            reader.readAsDataURL(file);
        }
    }

    /**
     * Mutates the live #drawer-save button directly (no full drawer re-render).
     * state: 'idle' | 'saving' | 'saved' | 'error'
     */
    _updateDrawerSaveBtn(state) {
        const btn = this.container.querySelector('#drawer-save');
        if (!btn) return;
        // Remove all state classes first
        btn.classList.remove('drawer-save--saving', 'drawer-save--saved', 'drawer-save--error');
        switch (state) {
            case 'saving':
                btn.disabled = true;
                btn.textContent = 'Saving…';
                btn.classList.add('drawer-save--saving');
                break;
            case 'saved':
                btn.disabled = true;
                btn.textContent = 'Saved ✓';
                btn.classList.add('drawer-save--saved');
                break;
            case 'error':
                btn.disabled = false;
                btn.textContent = 'Save Changes';
                btn.classList.add('drawer-save--error');
                break;
            default: // 'idle'
                btn.disabled = false;
                btn.textContent = 'Save Changes';
        }
    }

    /**
     * Returns the HTML for the publish action zone inside the drawer.
     * Based on item.status, shows different controls.
     */
    _renderPublishZone(item) {
        const status = (item.status || '').toLowerCase();

        if (status === 'scheduled' || status === 'pending') {
            return `<div class="drawer-publish-zone">
                <button id="drawer-publish-now" class="btn btn-glass btn-sm">Publish Now</button>
            </div>`;
        }

        if (status === 'failed' || status === 'dead_letter') {
            const errorMsg = item.last_error || 'Unknown error';
            const retryCount = Math.min(item.retry_count ?? 0, item.max_retries ?? 3);
            const maxRetries = item.max_retries ?? 3;
            const detail = status === 'dead_letter'
                ? `Stopped retrying after ${maxRetries} attempts`
                : `Attempt ${retryCount} of ${maxRetries}`;
            return `<div class="drawer-publish-zone drawer-publish-error">
                <p class="publish-error-msg"><strong>Publish failed:</strong> ${this._escHtml(errorMsg)}</p>
                <p class="publish-error-detail">${detail}</p>
                <div class="publish-error-actions">
                    <button id="drawer-publish-retry" class="btn btn-glass btn-sm">Retry</button>
                    <button id="drawer-publish-cancel" class="btn btn-danger btn-sm">Cancel</button>
                </div>
            </div>`;
        }

        if (status === 'published') {
            const pubDate = item.published_at ? new Date(item.published_at).toLocaleString() : '';
            return `<div class="drawer-publish-zone">
                <span class="publish-badge publish-badge--published">Published</span>
                ${pubDate ? `<span class="publish-badge-date">${pubDate}</span>` : ''}
            </div>`;
        }

        if (status === 'cancelled') {
            return `<div class="drawer-publish-zone">
                <span class="publish-badge publish-badge--cancelled">Cancelled</span>
            </div>`;
        }

        if (status === 'publishing') {
            return `<div class="drawer-publish-zone">
                <span class="publish-badge publish-badge--publishing">Publishing…</span>
            </div>`;
        }

        return '';
    }

    /** Simple HTML escape for user-facing error messages */
    _escHtml(str) {
        return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    async handlePublishNow() {
        if (!this.selectedItem) return;
        const id = this.selectedItem.id;

        // Show "Publishing…" feedback in the zone
        const zone = this.container.querySelector('.drawer-publish-zone');
        if (zone) zone.innerHTML = '<span class="publish-badge publish-badge--publishing">Publishing…</span>';

        try {
            await client.publishNow(id);

            // Update local state
            this.selectedItem.status = 'published';
            const item = this.items.find(i => String(i.id) === String(id));
            if (item) item.status = 'published';

            // Update the zone to show published badge
            if (zone) zone.innerHTML = '<span class="publish-badge publish-badge--published">Published</span>';
            this.toast('success', 'Published successfully.');

            await new Promise(r => setTimeout(r, 800));
            this.closeDrawer();
            this.refreshCalendarEvents();
        } catch (err) {
            const errorMsg = err.message || 'Unknown error';
            this.selectedItem.last_error = errorMsg;
            if (zone) {
                zone.className = 'drawer-publish-zone drawer-publish-error';
                zone.innerHTML = `
                    <p class="publish-error-msg"><strong>Publish failed:</strong> ${this._escHtml(errorMsg)}</p>
                    <div class="publish-error-actions">
                        <button id="drawer-publish-now" class="btn btn-glass btn-sm">Retry</button>
                    </div>`;
                const retryBtn = zone.querySelector('#drawer-publish-now');
                if (retryBtn) retryBtn.onclick = () => this.handlePublishNow();
            }
            this.toast('error', `Publish failed: ${err.message}`);
        }
    }

    async handleRetryPublish() {
        if (!this.selectedItem) return;
        const id = this.selectedItem.id;

        try {
            await client.retryPublish(id);

            this.selectedItem.status = 'scheduled';
            const item = this.items.find(i => String(i.id) === String(id));
            if (item) item.status = 'scheduled';

            this.toast('success', 'Queued for retry.');
            this.closeDrawer();
            this.refreshCalendarEvents();
        } catch (err) {
            this.toast('error', `Retry failed: ${err.message}`);
        }
    }

    async handleCancelPublish() {
        if (!this.selectedItem) return;
        const id = this.selectedItem.id;

        const confirmed = await this.confirmDelete({
            title: 'Cancel Publishing',
            message: 'Stop publishing this post? It will be moved to cancelled.'
        });
        if (!confirmed) return;

        try {
            await client.cancelPublish(id);

            this.selectedItem.status = 'cancelled';
            const item = this.items.find(i => String(i.id) === String(id));
            if (item) item.status = 'cancelled';

            this.toast('success', 'Publishing cancelled.');
            this.closeDrawer();
            this.refreshCalendarEvents();
        } catch (err) {
            this.toast('error', `Cancel failed: ${err.message}`);
        }
    }

    async handleSave() {
        if (!this.selectedItem || this.saveState === 'saving') return;

        const id = this.selectedItem.id;
        const item = this.items.find(i => String(i.id) === String(id)); // CAL-SAVE-002
        if (!item) {
            this.toast('error', 'Internal error: could not find the scheduled item. Please refresh.');
            return;
        }

        const rawDatetime = this.container.querySelector('#drawer-datetime')?.value || '';
        const content = this.container.querySelector('#edit-content')?.value ?? '';
        const title = this.container.querySelector('#drawer-title')?.value ?? '';

        // Guard: empty or invalid datetime — do NOT call toISOString on garbage
        if (!rawDatetime) {
            this.toast('error', 'Scheduled time is required.');
            this.updateSaveState('idle');
            return;
        }
        const parsedDate = new Date(rawDatetime);
        if (isNaN(parsedDate.getTime())) {
            this.toast('error', 'Scheduled time is invalid.');
            this.updateSaveState('idle');
            return;
        }
        const scheduled_for = parsedDate.toISOString();

        const before = { ...item };
        const after = { ...item, scheduled_for, content, title };
        const media_json = JSON.stringify(this.drawerMedia || []);
        const patch = { scheduled_for, content, title, media_json };

        this.lastWriteAction = { type: 'update', id, before, after, patch };

        // — Saving state —
        this.updateSaveState('saving');
        this._updateDrawerSaveBtn('saving');

        try {
            const res = await client.updateScheduledPost(id, patch);
            // client.request() already throws on !response.ok or data.ok===false.

            // Merge server's returned item, then overlay patch as source-of-truth
            // (title lives in content_items, not scheduled_posts — res.item won't have it)
            if (res.item) {
                Object.assign(item, res.item);
            } else {
                this.toast('error', 'Save succeeded but server returned no item data.');
            }
            item.scheduled_for = scheduled_for;
            item.content = content;
            item.title = title;

            this.refreshCalendarEvents();
            this.updateSaveState('saved', res?.requestId);

            // — Success state: show "Saved ✓", wait briefly, auto-close —
            this._updateDrawerSaveBtn('saved');
            this.toast('success', 'Changes saved.');

            await new Promise(resolve => setTimeout(resolve, 700));

            this.closeDrawer();
            // Reset save state after drawer closes so pill is clean next open
            this.updateSaveState('idle');
        } catch (err) {
            console.error('Save failed', err);
            // — Error state: re-enable button, keep drawer open —
            this.updateSaveState('error', err.requestId, err.message);
            this._updateDrawerSaveBtn('error');
            this.toast('error', `Save failed: ${err.message || 'Unknown error'}`);
        }
    }

    async handleDelete() {
        if (!this.selectedItem || this.saveState === 'saving') return;

        const confirmed = await this.confirmDelete({
            title: 'Delete Scheduled Post',
            message: 'This will permanently remove this scheduled post. This action cannot be undone.'
        });
        if (!confirmed) return;

        const id = this.selectedItem.id;
        const item = this.items.find(i => String(i.id) === String(id)); // CAL-SAVE-002
        if (!item) {
            this.toast('error', 'Internal error: could not find the scheduled item. Please refresh.');
            return;
        }

        const before = { ...item };
        this.lastWriteAction = { type: 'delete', id, before, after: null, patch: null };
        this.updateSaveState('saving');

        try {
            const res = await client.deleteScheduledPost(id);
            // Success = call resolved without exception.
            if (res && res.ok === false) throw new Error(res.error?.message || 'Server rejected delete');

            this.items = this.items.filter(i => String(i.id) !== String(id)); // CAL-SAVE-002
            this.refreshCalendarEvents();
            this.closeDrawer();
            this.updateSaveState('idle');
            this.toast('success', 'Deleted successfully.');
        } catch (err) {
            console.error('Delete failed', err);
            this.updateSaveState('error', err.requestId, err.message);
            this.toast('error', `Delete failed: ${err.message || 'Unknown error'}`);
        }
    }

    async handleEventDrop(info) {
        const id = info.event.id; // FullCalendar always provides string id
        // Personal-event drag: route to /api/personal-events instead of scheduledPosts.
        if (String(id).startsWith('pe:')) {
            const peId = String(id).slice(3);
            const ev = this.personalEvents.find(p => p.id === peId);
            if (!ev) {
                this.toast('error', 'Internal error: could not find the reminder. Please refresh.');
                info.revert();
                return;
            }
            const newStart = info.event.start.toISOString();
            this.updateSaveState('saving');
            try {
                const res = await client.updatePersonalEvent(peId, { starts_at: newStart });
                if (!res?.ok) throw new Error(res?.error || 'Move failed');
                ev.starts_at = newStart;
                if (res.item) Object.assign(ev, res.item);
                this.updateSaveState('saved', res?.requestId);
            } catch (err) {
                console.error('Reminder reschedule failed', err);
                info.revert();
                this.updateSaveState('error', err.requestId, err.message);
                this.toast('error', `Save failed: ${err.message || 'Could not move reminder'}`);
            }
            return;
        }

        const item = this.items.find(i => String(i.id) === String(id)); // CAL-SAVE-002
        if (!item) {
            this.toast('error', 'Internal error: could not find the scheduled item. Please refresh.');
            info.revert();
            return;
        }

        const before = { ...item };
        const newStart = info.event.start.toISOString();
        const after = { ...item, scheduled_for: newStart };
        const patch = { scheduled_for: newStart };

        this.lastWriteAction = { type: 'move', id, before, after, patch };
        this.updateSaveState('saving');

        try {
            const res = await client.updateScheduledPost(id, patch);
            // Success = call resolved without exception.
            if (res && res.ok === false) throw new Error(res.error?.message || 'Server rejected move');

            item.scheduled_for = newStart;
            this.updateSaveState('saved', res?.requestId);
        } catch (err) {
            console.error('Reschedule failed', err);
            info.revert();
            this.updateSaveState('error', err.requestId, err.message);
            this.toast('error', `Save failed: ${err.message || 'Could not reschedule post'}`);
        }
    }

    showFeedback(msg, isError = false) {
        const fb = this.container.querySelector('#drawer-feedback');
        if (!fb) return;
        fb.textContent = msg;
        fb.className = `calendar-feedback ${isError ? 'error' : 'success'}`;
    }

    /**
     * Open the personal-event modal in edit mode for a clicked FullCalendar
     * event (id starts with `pe:`). Handles save/delete/done locally so we
     * don't need to refetch the entire window.
     */
    async openPersonalEditor(fcEvent) {
        const peId = String(fcEvent.id).slice(3);
        const event = this.personalEvents.find(p => p.id === peId)
            || (fcEvent.extendedProps && fcEvent.extendedProps.kind === 'personal' ? { ...fcEvent.extendedProps, id: peId } : null);
        if (!event) {
            this.toast('error', 'Reminder details not loaded — try refreshing.');
            return;
        }
        const result = await openPersonalEventModal({ event });
        if (result?.action === 'saved' && result.item) {
            this.personalEvents = this._upsertPersonal(this.personalEvents, result.item);
            this.refreshCalendarEvents();
            this.toast('success', 'Reminder updated.');
        } else if (result?.action === 'deleted') {
            this.personalEvents = this.personalEvents.filter(p => p.id !== result.id);
            this.refreshCalendarEvents();
            this.toast('success', 'Reminder deleted.');
        }
    }

    truncate(str, n) {
        return (str.length > n) ? str.substr(0, n - 1) + '...' : str;
    }

    renderSaveStatus() {
        switch (this.saveState) {
            case 'saving': return '<span class="spinner-small"></span> Saving...';
            case 'saved': return '<span>✓ Saved</span>';
            case 'error': return '<span>Save failed</span> <button id="calendar-retry" class="link-btn">Retry</button>';
            default: return '<span>Up to date</span>';
        }
    }

    updateSaveState(state, requestId = null, error = null) {
        this.saveState = state;
        this.lastRequestId = requestId;
        this.lastErrorMessage = error;

        const pill = this.container.querySelector('#calendar-savepill');
        if (pill) {
            pill.className = `calendar-savepill status-${state}`;
            pill.innerHTML = this.renderSaveStatus();

            // Re-bind retry if error
            if (state === 'error') {
                const retryBtn = pill.querySelector('#calendar-retry');
                if (retryBtn) retryBtn.onclick = () => this.handleRetry();
            }
        }

        const undoBtn = this.container.querySelector('#calendar-undo');
        if (undoBtn) {
            undoBtn.disabled = !this.lastWriteAction || (this.lastWriteAction.type === 'delete');
        }

        if (state === 'saved') {
            setTimeout(() => {
                if (this.saveState === 'saved') {
                    this.updateSaveState('idle');
                }
            }, 3000);
        }
    }

    async performUndo() {
        if (!this.lastWriteAction) return;
        const action = this.lastWriteAction;

        this.updateSaveState('saving');
        try {
            let res;
            if (action.type === 'move' || action.type === 'update') {
                res = await client.updateScheduledPost(action.id, action.before);
            } else if (action.type === 'delete') {
                // Disable undo for delete unless we have re-insert support
                throw new Error('Undo delete not supported in this version.');
            }

            // Success = call resolved without exception.
            if (res && res.ok === false) throw new Error(res.error?.message || 'Server rejected undo');

            const idx = this.items.findIndex(i => String(i.id) === String(action.id)); // CAL-SAVE-002
            if (idx !== -1) {
                this.items[idx] = { ...this.items[idx], ...action.before };
            }
            this.refreshCalendarEvents();
            this.lastWriteAction = null;
            this.updateSaveState('saved', res?.requestId);
        } catch (err) {
            console.error('Undo failed', err);
            this.updateSaveState('error', err.requestId, err.message);
            this.toast('error', `Undo failed: ${err.message || 'Unknown error'}`);
        }
    }

    async handleRetry() {
        if (!this.lastWriteAction) return;
        const action = this.lastWriteAction;

        this.updateSaveState('saving');
        try {
            let res;
            if (action.type === 'move' || action.type === 'update') {
                res = await client.updateScheduledPost(action.id, action.patch);
            } else if (action.type === 'delete') {
                res = await client.deleteScheduledPost(action.id);
            }

            // Success = call resolved without exception.
            if (res && res.ok === false) throw new Error(res.error?.message || 'Server rejected retry');

            // Apply local state for retry of delete
            if (action.type === 'delete') {
                this.items = this.items.filter(i => String(i.id) !== String(action.id)); // CAL-SAVE-002
                this.refreshCalendarEvents();
                this.closeDrawer();
            } else {
                const idx = this.items.findIndex(i => String(i.id) === String(action.id)); // CAL-SAVE-002
                if (idx !== -1) {
                    this.items[idx] = { ...this.items[idx], ...action.patch };
                }
                this.refreshCalendarEvents();
            }
            this.updateSaveState('saved', res?.requestId);
        } catch (err) {
            this.updateSaveState('error', err.requestId, err.message);
            this.toast('error', `Retry failed: ${err.message || 'Unknown error'}`);
        }
    }

    unmount() {
        if (this.calendar) {
            this.calendar.destroy();
            this.calendar = null;
        }
        if (this.container) {
            this.container.innerHTML = '';
        }
        // Clean up failed posts drawer
        if (this._failedDrawer) {
            this._failedDrawer.destroy();
            this._failedDrawer = null;
        }
        // Clean up toast stack
        const stack = document.getElementById('calendar-toast-stack');
        if (stack) stack.remove();
        this._toastStack = null;
    }
}
